/**
 * SysOps Panel entry point.
 *
 * Provider order:
 *   MsalProvider → ApiClientProvider → AuthProvider → App
 *
 * AuthProvider depends on the api client to fetch role / permissions, so
 * ApiClientProvider mounts above it.
 */

import React from 'react';
import ReactDOM from 'react-dom/client';

import { MsalProvider } from '@/auth/MsalProvider';
import { ApiClientProvider } from '@/contexts/ApiClientContext';
import { AuthProvider } from '@/contexts/AuthContext';
import { App } from '@/App';
import { APP_VERSION, APP_BUILD_TIME, APP_NAME } from '@/constants/version';

import '@/styles/tokens.css';
import '@/styles/globals.css';
// The shared panel shell (sidebar, top bar, home, edit panel), after globals so it wins ties.
import '@retail-ops/panel-shell/style.css';
import { installSupportCapture } from '@retail-ops/support-report';
import { urlHasAuthResponse, runMsalRedirectBridge } from '@/auth/msalRedirectBridge';

// "Report a problem": start the background console / error / network buffers before the first screen.
installSupportCapture();

if (typeof window !== 'undefined') {
  // eslint-disable-next-line no-console
  console.info(`[${APP_NAME}] v${APP_VERSION} — booted ${APP_BUILD_TIME}`);
}

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('#root missing from index.html');

const startApp = (): void => {
  ReactDOM.createRoot(rootEl).render(
    <React.StrictMode>
      <MsalProvider>
        <ApiClientProvider>
          <AuthProvider>
            <App />
          </AuthProvider>
        </ApiClientProvider>
      </MsalProvider>
    </React.StrictMode>,
  );
};

// MSAL v5: when this page load is the sign-in return (popup, hidden iframe or full-page redirect),
// hand the response to MSAL instead of starting the app here. See msalRedirectBridge.ts.
if (urlHasAuthResponse()) {
  void runMsalRedirectBridge().then((handled) => {
    if (!handled) startApp();
  });
} else {
  startApp();
}
